import { z } from 'zod';
/**
 * GET /api/dashboard — the commander's numbers (REQUIREMENTS §2.9
 * "Dashboards: issued vs available, unsigned forms, overdue returns,
 * loss/damage by unit").
 *
 * Scoped like the roster: a commander gets the units they command, the
 * storeroom gets the company. Every count here is derived from the same
 * open-assignment read, so the tiles and the per-unit table always agree.
 */
export const unitStatsSchema = z.object({
    /** Soldiers on the roster (active accounts). */
    soldiers: z.number().int().min(0),
    /** Open assignments — everything still on someone's account. */
    openItems: z.number().int().min(0),
    /** Forms awaiting the soldier's signature. */
    unsignedForms: z.number().int().min(0),
    /** Open assignments past their due date (own or the mission's end). */
    overdue: z.number().int().min(0),
    lost: z.number().int().min(0),
    damaged: z.number().int().min(0),
    inRotation: z.number().int().min(0),
});
export const dashboardUnitSchema = unitStatsSchema.extend({
    unitId: z.string().min(1),
    name: z.string(),
    type: z.enum(['platoon', 'team']),
    parentId: z.string().nullable(),
});
/** One pending form, oldest first: who has not signed yet. */
export const unsignedFormSchema = z.object({
    id: z.string().min(1),
    soldierUid: z.string().min(1),
    soldierName: z.string(),
    issuerName: z.string(),
    unitName: z.string().nullable(),
    lineCount: z.number().int().min(0),
    issuedAt: z.string().nullable(),
});
/** Issued vs available per catalog category, company-wide (storeroom only). */
export const categoryStockSchema = z.object({
    category: z.string().min(1),
    total: z.number().int().min(0),
    available: z.number().int().min(0),
    issued: z.number().int().min(0),
    inRotation: z.number().int().min(0),
    lost: z.number().int().min(0),
    damaged: z.number().int().min(0),
});
export const DASHBOARD_LIST_LIMIT = 20;
export const dashboardResponseSchema = z.object({
    /** Server time, ISO — the page says when its numbers are from. */
    generatedAt: z.string(),
    totals: unitStatsSchema,
    /** Platoons first, each followed by its teams, in org order. Only units in scope. */
    units: z.array(dashboardUnitSchema),
    /** Oldest DASHBOARD_LIST_LIMIT pending forms in scope. */
    unsignedForms: z.array(unsignedFormSchema),
    /** Present for company-wide roles only; a commander's stock is not a company number. */
    stock: z.array(categoryStockSchema).nullable(),
});
//# sourceMappingURL=dashboard.js.map